<template>
    <div class="product-content">
        <div class="add-button">
            <el-button type="primary" @click="add">新增产品</el-button>
        </div>

        <div class="table">
            <div class="title">产品管理</div>

            <el-table :data="tableData" style="width: 100%">
                <el-table-column prop="order" label="序号" />
                <el-table-column prop="name" label="产品名称" />
                <el-table-column prop="cover" label="封面">
                    <template #default="{ row }">
                        <el-image preview-teleported :src="`${row.cover}`"
                            :preview-src-list="[`${row.cover}`]" />
                    </template>
                </el-table-column>
                <el-table-column prop="describeText" label="产品描述" />
                <el-table-column prop="cover" label="产品类型">
                    <template #default="{ row }">
                        <el-tag v-for="item in row.typeTags" :key="item" class="margin-left-5">
                            {{ item }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="cover" label="是否属于热卖产品">
                    <template #default="{ row }">
                        <el-switch v-model="row.hot" inline-prompt active-text="是" inactive-text="否" :active-value="1"
                            :inactive-value="0" disabled />
                    </template>
                </el-table-column>
                <el-table-column prop="createTime" label="发布时间" />
                <el-table-column prop="updateTime" label="更新时间" />
                <el-table-column label="操作">
                    <template #default="{ row }">
                        <el-button size="small" type="success"
                            @click="modifyProduct(row.id)">修改</el-button>
                        <el-button size="small" type="danger" @click="onDeleteHandler(row.id)">
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <div class="page">
            <Pagination :page-size="pageSize" v-model:current-page="currentPage" :total-page="totalPage"
                @current-page-change="pageChange"></Pagination>
        </div>
    </div>

    <el-drawer v-model="isOpendrawer" title="新增产品" size="50%">
        <el-form ref="ruleFormRef" style="max-width: 600px" :model="ruleForm" :rules="disabled ? null : rules"
            label-width="auto" :disabled="disabled">

            <el-form-item label="产品名称" prop="name">
                <el-input v-model="ruleForm.name" />
            </el-form-item>

            <el-form-item label="产品描述" prop="describeText">
                <el-input v-model="ruleForm.describeText" type="textarea" />
            </el-form-item>

            <el-form-item label="产品封面" prop="cover">
                <el-upload class="avatar-uploader" :action="`${ConstantField.SERVER}/product/admin/uploadImages`"
                    :show-file-list="false" :on-success="handleCoverUploadSuccess" :before-upload="beforeImageUpload"
                    :on-error="onUploadError" :data="{ imageType: 'productCover' }" with-credentials>
                    <img v-if="ruleForm.cover" :src="`${ruleForm.cover}`" class="avatar"
                        style="max-width: 178px; max-height: 178px;" />
                    <el-icon v-else class="avatar-uploader-icon">
                        <Plus />
                    </el-icon>
                </el-upload>
            </el-form-item>

            <el-form-item label="产品图片" prop="images">
                <el-upload v-model:file-list="productImageList" :before-upload="beforeImageUpload"
                    :on-error="onUploadError" :on-success="handleImagesUploadSuccess" :on-remove="handleRemoveImage"
                    :action="`${ConstantField.SERVER}/product/admin/uploadImages`" list-type="picture-card"
                    :data="{ imageType: 'productImages' }" with-credentials>
                    <el-icon>
                        <Plus />
                    </el-icon>
                </el-upload>
            </el-form-item>

            <el-form-item label="产品详情内容" prop="content">
                <Editor ref="editorRef" v-model:value-html="valueHtml" :mode="'default'" :placeholder="'请输入产品详细介绍内容'"
                    @on-change="onChange" :disabled="disabled">
                </Editor>
            </el-form-item>

            <el-form-item label="产品类别" prop="typeId">
                <el-cascader v-model="ruleForm.typeId" :options="types" :props="{ label: 'name', value: 'id' }"
                    placeholder="请选择类别" />
            </el-form-item>

            <el-form-item label="是否为热卖产品" prop="hot">
                <el-switch v-model="ruleForm.hot" inline-prompt active-text="是" inactive-text="否" :active-value="1"
                    :inactive-value="0" />
            </el-form-item>

            <el-form-item>
                <el-button type="primary" @click="submitForm(ruleFormRef)" v-if="formType === 'add'">
                    提交
                </el-button>
                <el-button type="success" @click="onUpdateProductHandler(ruleFormRef)"
                    v-else-if="formType === 'update'">
                    更新
                </el-button>
            </el-form-item>
        </el-form>
    </el-drawer>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, reactive, Reactive, ref, Ref } from 'vue';
import Pagination from '../Pagination.vue';
import { Action, FormType } from '@/typeDefine/CommonTypes';
import { Images, Product, ProductTypes } from '@/typeDefine/EntityType';
import { Api } from '@/http/Api';
import { BaseResponse, GetProductsResponse, GetProductTypesResponse } from '@/typeDefine/HttpResponseType';
import { UtilMethods } from '@/util/UtilMethods';
import { ResponseCode } from '@/http/ResponseCode';
import { ElMessage, ElMessageBox, FormInstance, FormRules, UploadFile, UploadFiles, UploadProps, UploadRawFile, UploadUserFile } from 'element-plus';
import Editor, { EditorComponentType } from './Editor.vue';
import { Loading } from '@/util/Loading';
import { ConstantField } from '@/constant/ConstantField';
import { AddProductRequest } from '@/typeDefine/HttpRequestType';

interface ProductExtend extends Product {
    typeTags?: string[]
}

//当前页
const currentPage: Ref<number> = ref(1)
//页大小
const pageSize: Ref<number> = ref(10)
//总页数
const totalPage: Ref<number> = ref(0)

const tableData: Ref<ProductExtend[]> = ref([])

const types: Ref<ProductTypes[]> = ref([])

const isOpendrawer: Ref<boolean> = ref(false)

const disabled: Ref<boolean> = ref(false)

const ruleFormRef: Ref<FormInstance | undefined> = ref()

//当前表单状态
const formType: Ref<FormType> = ref('none')

//当前选中的产品行id
const currentClickId: Ref<number> = ref(0)

const ruleForm: Reactive<AddProductRequest> = reactive({
    name: '',
    describeText: '',
    content: '',
    hot: 0,
    cover: '',
    images: [],
    typeId: [],
})

//上传的产品图列表
const productImageList: Ref<UploadUserFile[]> = ref([])

/**
* 富文本相关字段
*/
const editorRef: Ref<EditorComponentType | null> = ref(null)
const valueHtml: Ref<string> = ref('')

const rules: Reactive<FormRules<AddProductRequest>> = reactive({
    name: [
        {
            required: true, message: '请输入标题', trigger: 'blur',
        },
        {
            max: 255, message: '最大输入255个字符', trigger: 'blur'
        }
    ],
    describeText: [
        {
            max: 500, message: '最大输入500个字符', trigger: 'blur'
        }
    ],
    content: [
        {
            required: true, message: '请输入内容', trigger: 'blur',
        },
    ],
    cover: [
        {
            required: true, message: '请上传一张产品封面', trigger: 'blur',
        }
    ],
    typeId: [
        {
            required: true, message: '请选择产品类别', trigger: 'blur',
        }
    ]
})

onMounted(() => {
    requestProductTypes()
    requestProduct(currentPage.value)
})

//请求所有类别
const requestProductTypes: Action = async () => {
    const response: GetProductTypesResponse = await Api.instance.getProductTypes()

    UtilMethods.showMessageToast(response, false)

    if (response.code === ResponseCode.OK) {
        types.value = response.data.treeType
    }
}

//请求所有产品
const requestProduct: Action<[number]> = async (startPage: number) => {
    const response: GetProductsResponse = await Api.instance.getProductByPage({
        startPage
    })

    UtilMethods.showMessageToast(response, false)

    if (response.code === ResponseCode.OK) {
        tableData.value = response.data.products
        totalPage.value = response.data.totalPage

        let i: number = 0
        tableData.value = tableData.value.map(item => {
            return {
                order: ++i,
                ...item,
                typeTags: useProductTypePath(types.value,item.productType,item => item.name)
            }
        })
    }
}

//设置表单值
const setFormValue: Action<[AddProductRequest, UploadUserFile[], Action<[string?]>, string?]> = ({ name, describeText, content, hot, cover, typeId, images }, imageList,callback,param) => {
    ruleForm.name = name
    ruleForm.describeText = describeText
    ruleForm.content = content
    ruleForm.hot = hot
    ruleForm.cover = cover
    ruleForm.typeId = typeId
    ruleForm.images = images

    productImageList.value = imageList

    callback(param)
}

function useProductTypePath<T>(
  types: ProductTypes[],
  target: ProductTypes,
  mapper: (item: ProductTypes) => T
): T[] {
  for (const item of types) {
    if (item.id === target.parentId) {
      return [mapper(item), mapper(target)]
    }

    if (Array.isArray(item.children) && item.children.length > 0) {
      const childPath = useProductTypePath(item.children, target, mapper)
      if (childPath.length > 0) {
        return [mapper(item), ...childPath]
      }
    }
  }
  return []
}

const add: Action = () => {
    isOpendrawer.value = true
    formType.value = 'add'

    setFormValue({name: '', describeText: '', content: '',hot: 0, cover: '',typeId: [], images: []},[],() => {
        editorRef.value?.clear()
    })
}

const pageChange: Action<[number]> = (value: number) => {
    requestProduct(value)
}

//上传图片前校验
const beforeImageUpload: UploadProps['beforeUpload'] = (rawFile) => {
    if (!rawFile.type.startsWith("image")) {
        ElMessage.error('请上传图片类型文件')
        return false
    }

    Loading.instance.open('上传中...')
    return true
}

//上传失败
const onUploadError: UploadProps['onError'] = (error) => {
    Loading.instance.close()
    ElMessage.error(error.message)
}

//封面上传成功
const handleCoverUploadSuccess: UploadProps['onSuccess'] = (response, uploadFile) => {
    Loading.instance.close()

    ruleForm.cover = response.data.images[0].path
}

//其他图片上传成功
const handleImagesUploadSuccess: UploadProps['onSuccess'] = (response: any, uploadFile) => {
    Loading.instance.close()

    for (const item of response.data.images) {
        ruleForm.images.push(item.id)
    }

}

//移除图片
const handleRemoveImage: UploadProps['onRemove'] = (uploadFile: UploadFile, uploadFiles: UploadFiles) => {
    //同步删除ruleForm.images中的图片id
    if (uploadFile.response) {
        const response: any = uploadFile.response

        const index: number = ruleForm.images.findIndex(item => item === response.data.images[0].id)

        if (index > -1) {
            ruleForm.images.splice(index, 1)
        }
    }else if(uploadFiles.every(item => item.name)) {
        ruleForm.images = uploadFiles.map(item => Number(item.name))
    }
}

//监听富文本的文字
const onChange: Action<[string]> = (value: string) => {
    ruleForm.content = value
}

const refresh: Action = () => {
    currentPage.value = 1
    requestProduct(currentPage.value)
}

//提交
const submitForm: Action<[FormInstance | undefined]> = async (formEl) => {
    if (!formEl) return
    await formEl.validate(async (valid, fields) => {

        if (!valid) {
            return
        }

        const { name, describeText, hot, cover, images, typeId } = ruleForm
        const response: BaseResponse = await Api.instance.addProduct({
            name,
            describeText,
            hot,
            cover,
            images,
            typeId,
            content: valueHtml.value
        })

        UtilMethods.showMessageToast(response)

        if (response.code === ResponseCode.OK) {
            refresh()
        }
    })
}

//打开更新窗口
const modifyProduct: Action<[number]> = (id: number) => {
    isOpendrawer.value = true
    formType.value = 'update'
    currentClickId.value = id

    const product: ProductExtend | undefined = tableData.value.find(item => item.id === id)

    if(!product) {
        return
    }

    const {name,describeText,content,hot,cover} = product

    setFormValue({
        name, describeText, content, hot, cover,
        typeId: useProductTypePath(types.value, product.productType,item => item.id), //productTypeIdRecursion(types.value, product.productType),
        images: product.images.map(item => item.id)
    },
        product.images.map(item => {
            return {
                name: `${item.id}`,
                url: `${item.path}`
            }
        }), () => {
            nextTick(() => {
                editorRef.value?.setHtml(product.content)
            })
        })
}

//更新
const onUpdateProductHandler: Action<[FormInstance | undefined]> = async (formEl) => {
    if (!formEl) return

    await formEl.validate(async (valid, fields) => {
        if (!valid) {
            return
        }
        const { name, content, describeText, cover, images, typeId, hot } = ruleForm

        const response: BaseResponse = await Api.instance.updateProduct({
            id: currentClickId.value,
            name,
            describeText,
            content: valueHtml.value,
            cover,
            hot,
            images,
            typeId
        })

        UtilMethods.showMessageToast(response)

        if (response.code === ResponseCode.OK) {
            refresh()
        }
    })
}

//删除
const onDeleteHandler: Action<[number]> = async (id) => {
    ElMessageBox.confirm('将执行删除该产品操作,是否确定?', '删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
    }).then(async () => {
        const response: BaseResponse = await Api.instance.deleteProduct({
            id,
        })

        UtilMethods.showMessageToast(response)

        if (response.code === ResponseCode.OK) {
            refresh()
        }
    }).catch(() => { })
}
</script>

<style lang="css" scoped>
.product-content .add-button {
    margin: 20px 0;
}

.product-content .table .title {
    font-size: 20px;
    font-weight: bold;
}

.product-content .page {
    display: flex;
    justify-content: right;
    align-items: center;
    margin: 0 20px;
}
</style>

<style>
.avatar-uploader .el-upload {
    border: 1px dashed var(--el-border-color);
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: var(--el-transition-duration-fast);
}

.avatar-uploader .el-upload:hover {
    border-color: var(--el-color-primary);
}

.el-icon.avatar-uploader-icon {
    font-size: 28px;
    color: #8c939d;
    width: 178px;
    height: 178px;
    text-align: center;
}

.product-content img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px 0;
    object-fit: contain;
}
</style>